"use client";

import config from "@/lib/config";
import ehjzleeInstance from "@/lib/ehjzleeInstance";
import { useSearchParams } from "next/navigation";
import { useState } from "react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { format } from "date-fns";

export const useGetProps = (
  size?: number,
  bounds?: google.maps.LatLngBoundsLiteral
) => {
  const searchParams = useSearchParams();
  const [mapValues, setMapValues] = useState(bounds);

  const query = useQuery({
    queryKey: [
      mapValues?.east ? `all-props-map` : "all-props",
      searchParams.get("page"),
      searchParams.getAll("cityId"),
      searchParams.get("from"),
      searchParams.get("to"),
      searchParams.get("numOfGuests"),
      searchParams.get("classification"),
      searchParams.get("minPrice"),
      searchParams.get("maxPrice"),
      searchParams.getAll("rating"),
      searchParams.get("numOfBedrooms"),
      searchParams.get("numOfSingleBeds"),
      searchParams.get("numOfMasterBeds"),
      searchParams.get("numOfBathrooms"),
      searchParams.getAll("entertainment"),
      mapValues,
      size,
    ],
    queryFn: () =>
      getProps(
        searchParams.get("page") || "1",
        searchParams.getAll("cityId"),
        searchParams.get("from"),
        searchParams.get("to"),
        searchParams.get("numOfGuests"),
        mapValues,
        searchParams.get("classification"),
        searchParams.get("minPrice"),
        searchParams.get("maxPrice"),
        searchParams.getAll("rating"),
        searchParams.get("numOfBedrooms"),
        searchParams.get("numOfSingleBeds"),
        searchParams.get("numOfMasterBeds"),
        searchParams.get("numOfBathrooms"),
        searchParams.getAll("entertainment"),
        size ?? 12
      ),
  });

  return { ...query, setMapValues };
};

export const useGetPropsMap = (
  size?: number,
  bounds?: google.maps.LatLngBoundsLiteral
) => {
  const searchParams = useSearchParams();
  const [mapValues, setMapValues] = useState(bounds);

  const query = useQuery({
    queryKey: [
      "get-props-in-map",
      searchParams.getAll("cityId"),
      searchParams.get("from"),
      searchParams.get("to"),
      searchParams.get("numOfGuests"),
      searchParams.get("classification"),
      searchParams.get("minPrice"),
      searchParams.get("maxPrice"),
      searchParams.getAll("rating"),
      searchParams.get("numOfBedrooms"),
      searchParams.get("numOfSingleBeds"),
      searchParams.get("numOfMasterBeds"),
      searchParams.get("numOfBathrooms"),
      searchParams.getAll("entertainment"),
      mapValues,
      size,
    ],
    queryFn: () =>
      getProps(
        "1",
        searchParams.getAll("cityId"),
        searchParams.get("from"),
        searchParams.get("to"),
        searchParams.get("numOfGuests"),
        mapValues,
        searchParams.get("classification"),
        searchParams.get("minPrice"),
        searchParams.get("maxPrice"),
        searchParams.getAll("rating"),
        searchParams.get("numOfBedrooms"),
        searchParams.get("numOfSingleBeds"),
        searchParams.get("numOfMasterBeds"),
        searchParams.get("numOfBathrooms"),
        searchParams.getAll("entertainment"),
        size ?? 12
      ),
  });

  return { ...query, setMapValues };
};

const getProps = async (
  page: string,
  cityId?: string[] | null,
  checkInDate?: string | null,
  checkOutDate?: string | null,
  numOfGuests?: string | null,
  bounds?: google.maps.LatLngBoundsLiteral,
  classification?: string | null,
  minPrice?: string | null,
  maxPrice?: string | null,
  rating?: string[] | null,
  numOfBedrooms?: string | null,
  numOfSingleBeds?: string | null,
  numOfMasterBeds?: string | null,
  numOfBathrooms?: string | null,
  entertainment?: string[] | null,
  size?: number
) => {
  try {
    const params: any = {};

    // Set default size to 12 if not provided
    params.size = size ?? 12;

    if (cityId) {
      Object.assign(params, { cityId });
    }

    if (checkInDate) {
      Object.assign(params, { checkInDate: format(new Date(checkInDate), "yyyy-MM-dd") });
    }

    if (checkOutDate) {
      Object.assign(params, { checkOutDate });
    }

    if (numOfGuests) {
      Object.assign(params, { numOfGuests });
    }

    if (classification) {
      Object.assign(params, { classification });
    }

    if (minPrice) {
      Object.assign(params, { minPrice });
    }

    if (maxPrice) {
      Object.assign(params, { maxPrice });
    }

    if (rating) {
      Object.assign(params, { rating });
    }

    if (numOfBedrooms) {
      Object.assign(params, { numOfBedrooms });
    }

    if (numOfSingleBeds) {
      Object.assign(params, { numOfSingleBeds });
    }

    if (numOfMasterBeds) {
      Object.assign(params, { numOfMasterBeds });
    }

    if (numOfBathrooms) {
      Object.assign(params, { numOfBathrooms });
    }

    if (entertainment && entertainment.length > 0) {
      Object.assign(params, { entertainment });
    }

    if (bounds) {
      Object.assign(params, { ...bounds });
    }

    // Build query string manually to ensure proper entertainment parameter format
    const queryParams = new URLSearchParams();
    queryParams.append('page', page);
    
    // Add all other parameters
    Object.entries(params).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        // Handle arrays (like entertainment) as repeated parameters
        value.forEach(item => queryParams.append(key, item));
      } else {
        queryParams.append(key, String(value));
      }
    });

    const res = await ehjzleeInstance.get(`${config.props}?${queryParams.toString()}`);
    return res.data;
  } catch (err) {
    return [];
  }
};

export const useGetInfiniteProps = () => {
  const q = useInfiniteQuery<{
    page: number;
    size: number;
    count: number;
    pagesCount: number;
    properties: Property[];
  }>({
    queryKey: ["props-infinite"],
    queryFn: ({ pageParam }) => getProps(pageParam as string),
    initialPageParam: 1,
    getNextPageParam: ({ page, pagesCount }) =>
      page >= pagesCount ? undefined : ++page,
  });

  return q;
};
